<template>
  <BaseModal :modalTitle="$t('area.pricing-segment.create.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <UiComboboxVee
        class="mt-4"
        name="currencyCode"
        :label="$t('term.currency')"
        :options="currencyOptions"
        v-model="form.currencyCode" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useModalStore } from '@/stores/modalStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { mapActions, mapState } from 'pinia'
import router from '@/router'
import { useToast } from '@/globals/typescript/utils/toast'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreatePricingSegmentModal',

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        name: '',
        currencyCode: '' as TLabManagementCurrencyCode | '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAccountStore, ['availableCurrencies', 'defaultCurrencyCode']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    currencyOptions() {
      return this.availableCurrencies
        .map((currency) => ({
          value: currency.code as TLabManagementCurrencyCode,
          text: `${(currency.name as Record<string, string>)[this.currentLocale] || currency.name['en']} (${currency.code})`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  watch: {
    'form.name'() {
      this.errorText = ''
    },
  },

  methods: {
    ...mapActions(usePricingSegmentStore, ['createPricingSegment']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        const id = await this.createPricingSegment({
          name: this.form.name,
          currencyCode: this.form.currencyCode || this.defaultCurrencyCode,
        })
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Prissegment oprettet'))
        this.closeTop()
        router.push({
          name: 'PricingSegmentEditGeneral',
          params: { segmentID: id },
        })
      } catch {
        this.errorText = this.$t('error.common.standard') as string
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.currencyCode = this.defaultCurrencyCode
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

